'use client'

import { useState, SubmitEvent } from 'react'
import { m } from 'framer-motion'
import { Send, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRecaptcha } from '@/hooks/useRecaptcha'
import GlowCaretField from '@/components/ui/GlowCaretField'
import { useConsent } from '@/lib/consent-manager'

const fieldClassName =
  'w-full rounded-xl px-3.5 py-2 transition-all sm:px-4 sm:py-3 focus:ring-2 focus:ring-[var(--flagship-cyan)]/50 focus:outline-none'
const fieldStyle = {
  backgroundColor: 'rgba(255,255,255,0.04)',
  color: 'var(--flagship-text)',
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.15), inset 0 1px 0 0 rgba(255,255,255,0.15)',
}

export default function SiteContactForm() {
  const t = useTranslations('homepage.contact.form')
  const { isConsentRequired, openSettings } = useConsent()
  const {
    isLoaded,
    isLoading,
    error: recaptchaError,
    executeRecaptcha,
  } = useRecaptcha(
    process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '6LfB16srAAAAAJ5XPgX1dmu9y7h-KooC7HvEhTnv'
  )
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  })

  const handleSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)

    if (isConsentRequired()) {
      openSettings()
      return
    }

    setFormState('loading')

    try {
      const recaptchaToken = await executeRecaptcha('contact_form')

      if (!recaptchaToken) {
        setError(t('recaptchaError'))
        setFormState('idle')
        return
      }

      const response = await fetch('/api/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
          timestamp: new Date().toISOString(),
        }),
      })

      if (response.ok) {
        setFormState('success')
        setFormData({ name: '', email: '', phone: '', message: '' })
      } else {
        setFormState('error')
      }
    } catch {
      setFormState('error')
    }
  }

  if (formState === 'success') {
    return (
      <m.div
        className="flex min-h-100 flex-col items-center justify-center rounded-2xl p-8 text-center"
        style={{
          backgroundColor: 'rgba(255,255,255,0.04)',
          boxShadow:
            'inset 0 0 0 1px rgba(255,255,255,0.25), inset 0 1.5px 0 0 rgba(255,255,255,0.35), inset 0 0 50px -28px var(--flagship-cyan)',
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <m.div
          className="mb-6 flex h-20 w-20 items-center justify-center rounded-full"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--flagship-cyan) 18%, transparent)',
            boxShadow:
              'inset 0 1px 0 0 rgba(255,255,255,0.3), inset 0 0 0 1px color-mix(in srgb, var(--flagship-cyan) 45%, transparent), 0 0 24px -4px var(--flagship-cyan)',
          }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
        >
          <svg
            className="h-10 w-10"
            style={{ color: 'var(--flagship-cyan)' }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </m.div>
        <h3 className="mb-2 [font-family:var(--font-ui)] text-2xl font-bold text-[var(--flagship-text)]">
          {t('successHeading')}
        </h3>
        <p className="mb-6 text-[var(--flagship-muted)]">{t('successBody')}</p>
        <button
          onClick={() => setFormState('idle')}
          className="font-medium"
          style={{ color: 'var(--flagship-cyan)' }}
        >
          {t('sendAnother')}
        </button>
      </m.div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-2.5 sm:space-y-5"
      aria-describedby={
        formState === 'error' || error || recaptchaError ? 'contact-form-error' : undefined
      }
    >
      <div className="grid gap-2.5 sm:grid-cols-2 sm:gap-5">
        <div>
          <label
            htmlFor="contact-name"
            className="mb-0.5 block text-sm font-medium text-[var(--flagship-text)] sm:mb-2"
          >
            {t('nameLabel')}
          </label>
          <GlowCaretField
            id="contact-name"
            type="text"
            required
            aria-required="true"
            placeholder={t('namePlaceholder')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={fieldClassName}
            style={fieldStyle}
          />
        </div>
        <div>
          <label
            htmlFor="contact-email"
            className="mb-0.5 block text-sm font-medium text-[var(--flagship-text)] sm:mb-2"
          >
            {t('emailFieldLabel')}
          </label>
          <GlowCaretField
            id="contact-email"
            // text + inputMode, not type="email": browsers hide the cursor position of email
            // fields, which the drawn caret needs. The pattern keeps built-in validation.
            type="text"
            inputMode="email"
            autoComplete="email"
            pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
            required
            aria-required="true"
            placeholder={t('emailPlaceholder')}
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={fieldClassName}
            style={fieldStyle}
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="contact-phone"
          className="mb-0.5 block text-sm font-medium text-[var(--flagship-text)] sm:mb-2"
        >
          {t('phoneFieldLabel')} <span className="opacity-60">{t('phoneOptional')}</span>
        </label>
        <GlowCaretField
          id="contact-phone"
          type="tel"
          placeholder={t('phonePlaceholder')}
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className={fieldClassName}
          style={fieldStyle}
        />
      </div>

      <div>
        <label
          htmlFor="contact-message"
          className="mb-0.5 block text-sm font-medium text-[var(--flagship-text)] sm:mb-2"
        >
          {t('messageLabel')}
        </label>
        <GlowCaretField
          multiline
          id="contact-message"
          rows={3}
          required
          aria-required="true"
          placeholder={t('messagePlaceholder')}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className={`resize-none ${fieldClassName}`}
          style={fieldStyle}
        />
      </div>

      {(formState === 'error' || error || recaptchaError) && (
        <p
          id="contact-form-error"
          className="text-sm"
          style={{ color: 'var(--flagship-pink)' }}
          role="alert"
        >
          {error || recaptchaError || t('genericError')}
        </p>
      )}

      <m.button
        type="submit"
        disabled={formState === 'loading' || isLoading || !isLoaded}
        className="group relative inline-flex h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-full px-8 [font-family:var(--font-ui)] text-lg font-bold text-[var(--flagship-text)] transition-transform duration-500 ease-out disabled:opacity-70 sm:h-14"
        style={{
          background:
            'linear-gradient(90deg, color-mix(in srgb, var(--flagship-cyan) 45%, transparent), color-mix(in srgb, var(--flagship-pink) 45%, transparent))',
          boxShadow:
            'inset 0 0 0 1px rgba(255,255,255,0.25), inset 0 1.5px 0 0 rgba(255,255,255,0.65), inset 0 -1px 0 0 rgba(255,255,255,0.06), 0 12px 30px -10px color-mix(in srgb, var(--flagship-pink) 45%, transparent)',
        }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* specular top sheen, like the glass cards */}
        <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full bg-gradient-to-b from-white/25 to-transparent" />
        {/* shimmer sweep on hover */}
        <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-full" />
        <span className="relative inline-flex items-center gap-2">
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('recaptchaLoading')}
            </>
          ) : formState === 'loading' ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('submitting')}
            </>
          ) : (
            <>
              {t('submit')}
              <Send className="h-5 w-5" />
            </>
          )}
        </span>
      </m.button>

      <p className="mt-2 text-center text-[11px] leading-snug text-[var(--flagship-muted)] sm:mt-4 sm:text-xs">
        {t.rich('recaptchaNotice', {
          privacy: (chunks) => (
            <a
              key="privacy"
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-70"
              style={{ color: 'var(--flagship-cyan)' }}
            >
              {chunks}
            </a>
          ),
          terms: (chunks) => (
            <a
              key="terms"
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-70"
              style={{ color: 'var(--flagship-cyan)' }}
            >
              {chunks}
            </a>
          ),
        })}
      </p>
    </form>
  )
}
